// src/oura/delivery/DocStepParts.tsx
//
// The deliverable's client document in its drawer, behind the Docs write
// switch (contract v1.3, H31; scope 2.18):
//
//   DocStepBlock     the doc the plan wants for this code, when it is not
//                    already the pinned action: "Create the doc" while no
//                    doc covers the code, "Read it, then share" while one
//                    sits unshared. Never both for one code, and never a
//                    second Create.
//   ApprovedPdfBlock the PDF of the approved version a named person filed
//                    to the client's Reports (B24), with who filed it and
//                    when.
//   createPrefillOf  what Create a doc opens with (lane 2's sheet).
//
// Each renders nothing without DOCS_WRITE_ON, so a build missing the write
// switch shows no Phase 2 control here.

import { ExternalLinkIcon, FileCheck2Icon, FilePlus2Icon, FileTextIcon } from "lucide-react";
import { shortDay, type Deliverable, type MainAction } from "@service/aeo/delivery";
import { myDate } from "@service/aeo/clientDocsRules";
import { DOCS_WRITE_ON, listApprovedReports, type ApprovedReport } from "@service/aeo/clientDocsWrite";
import { useDocsLoad } from "@oura/docs";
import { DrawerBlock, FOCUS_RING, PillButton, TYPE } from "../ui";
import { cn } from "../lib/cn";
import type { CreatePrefill } from "../components/docs/create/createModel";

/** What Create a doc opens with, from the pinned action or the step: the doc type, its codes, the outlet. */
export function createPrefillOf(d: Pick<Deliverable, "code" | "outlet" | "docStep">, a?: Pick<MainAction, "docType" | "codes"> | null): CreatePrefill {
    const step = d.docStep ?? null;
    return {
        docType: a?.docType ?? step?.docType ?? null,
        codes: a?.codes?.length ? a.codes : step?.codes.length ? step.codes : [d.code],
        outlet: step?.outlet ?? d.outlet ?? null,
    };
}

/** The doc step, when the pinned action is something else (a reply to read first, say). */
export function DocStepBlock({ d, onCreate, onOpenDoc }: { d: Deliverable; onCreate: (prefill: CreatePrefill) => void; onOpenDoc: (docId: string) => void }) {
    const step = d.docStep;
    if (!DOCS_WRITE_ON || !step || d.mainAction.kind === "create_doc" || d.group === "done") return null;
    const share = step.kind === "share" && step.docId;
    const due = step.expectedBy ? `due ${shortDay(step.expectedBy)}` : null;
    return (
        <DrawerBlock label={share ? `${step.docType} to share` : `${step.docType} to make`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="inline-flex min-w-0 flex-1 basis-40 items-center gap-2">
                    {share ? <FileTextIcon aria-hidden size={14} className="shrink-0 text-blue-600" /> : <FilePlus2Icon aria-hidden size={14} className="shrink-0 text-blue-600" />}
                    <span className="min-w-0 break-words">{step.docType}</span>
                </span>
                {share ? (
                    <PillButton size="sm" onClick={() => onOpenDoc(step.docId!)}>
                        Read it, then share
                    </PillButton>
                ) : (
                    <PillButton size="sm" icon={FilePlus2Icon} onClick={() => onCreate(createPrefillOf(d))}>
                        Create the doc
                    </PillButton>
                )}
            </div>
            <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>
                {[share ? "In the folder, not shared yet" : "Not made yet", due].filter(Boolean).join(" · ")}
            </p>
        </DrawerBlock>
    );
}

/**
 * The newest approved PDF for this deliverable: its own doc, or a filed doc
 * covering its code since its window began. The outlet counts only on a
 * deliverable of its own per outlet (a key ending in the outlet); one S2
 * deliverable for a single outlet covers every S2 doc.
 */
function approvedPdfFor(d: Pick<Deliverable, "key" | "code" | "month" | "outlet" | "document" | "docStep" | "window">, reports: readonly ApprovedReport[]): ApprovedReport | null {
    const ids = new Set([d.document?.docId, d.docStep?.docId].filter((x): x is string => Boolean(x)));
    const outlet = d.outlet && d.key !== `${d.code}-m${d.month}` ? d.outlet : null;
    const hits = reports.filter(
        (r) =>
            ids.has(r.documentId) ||
                (Boolean(r.codes?.includes(d.code)) && (!outlet || !r.outlet || r.outlet === outlet) && (!d.window || myDate(r.filedAt) >= d.window.start)),
    );
    return hits.sort((a, b) => b.filedAt.localeCompare(a.filedAt))[0] ?? null;
}

/** "Approved PDF": the filed version, who filed it and when, and the file. */
export function ApprovedPdfBlock({ businessId, d }: { businessId: string; d: Deliverable }) {
    const load = useDocsLoad<ApprovedReport[]>(DOCS_WRITE_ON ? `approved-reports:${businessId}` : null, () => listApprovedReports(businessId));
    if (!DOCS_WRITE_ON || !load.data) return null;
    const pdf = approvedPdfFor(d, load.data);
    if (!pdf) return null;
    return (
        <DrawerBlock label="Approved PDF">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="inline-flex min-w-0 flex-1 basis-40 items-center gap-2">
                    <FileCheck2Icon aria-hidden size={14} className="shrink-0 self-center text-emerald-600" />
                    <span className="min-w-0 break-words">{pdf.title}</span>
                </span>
                <a
                    href={pdf.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(TYPE.tc, "inline-flex shrink-0 items-center gap-1 rounded font-medium text-blue-700 hover:underline", FOCUS_RING)}
                >
                    Open the PDF
                    <ExternalLinkIcon aria-hidden size={12} />
                    <span className="sr-only">, opens in a new tab</span>
                </a>
            </div>
            <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                Filed {shortDay(myDate(pdf.filedAt))} by {pdf.filedBy} · in the client&rsquo;s Reports
            </p>
        </DrawerBlock>
    );
}
